import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidTenantName } from '../../../../shared-types/src/index.js';
import type {
  Partner,
  PartnerAdminView,
  PartnerCustomModelSummary
} from '../../api.js';
import { TabStrip } from '../../components/TabStrip.js';
import { useAsyncAction } from '../../hooks/index.js';
import type { AdminHttpClient } from '../../http.js';
import type { TenantAssignment, ToastMessage } from '../../types.js';
import { describeError } from '../../errors.js';
import { PartnerAdminsTab } from './PartnerAdminsTab.js';
import { PartnerConnectorTab } from './PartnerConnectorTab.js';
import { PartnerCreateForm } from './PartnerCreateForm.js';
import { PartnerModelScopeTab } from './PartnerModelScopeTab.js';
import { PartnerSettingsPanel } from './PartnerSettingsPanel.js';
import { PartnersTable } from './PartnersTable.js';

/** Parse a tenant-cap text input into a value or a validation failure.
 *  Empty → the supplied `emptyValue` (undefined on create = no cap; null on
 *  edit = clear cap). Non-numeric/negative → invalid (so a typo never silently
 *  becomes "unlimited" via NaN → null serialization). */
function parseCap(raw: string, emptyValue: undefined | null): { ok: true; value: number | undefined | null } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: emptyValue };
  if (!/^\d+$/.test(trimmed)) return { ok: false };
  return { ok: true, value: Number(trimmed) };
}

// The four management surfaces of the SELECTED partner, in the order an
// operator works through them: settle the partner, give it admins, then define
// what it may distribute. Creation and the partner table stay above the strip
// because they are how a partner gets selected in the first place.
const TABS = [
  { id: 'settings', label: 'partners.tabSettings' },
  { id: 'admins', label: 'partners.tabAdmins' },
  { id: 'modelScope', label: 'partners.tabModelScope' },
  { id: 'connectorScope', label: 'partners.tabConnectorScope' },
] as const;
type TabId = (typeof TABS)[number]['id'];

function partnerScopeKeyFromCatalogId(catalogModelId: string, tenantId?: string): string {
  if (tenantId) {
    const customPrefix = `${tenantId}-custom-`;
    if (catalogModelId.startsWith(customPrefix)) return `custom:${catalogModelId.slice(customPrefix.length)}`;
    const tenantPrefix = `${tenantId}-`;
    if (catalogModelId.startsWith(tenantPrefix)) return catalogModelId.slice(tenantPrefix.length);
  }
  const tenantScoped = /^tnt_[^-]+-(.+)$/.exec(catalogModelId);
  return tenantScoped?.[1] ?? catalogModelId;
}

/**
 * Platform-only Partners console: create partners, cap their tenant count,
 * designate partner admins, and set the model-preset scope a partner may
 * distribute to its tenants. The partner-admin's own console (logging in as a
 * partner, creating tenants, acting into them) is a separate surface.
 */
export function PartnersScreen({
  http,
  onToast,
  selectedTenantId
}: {
  http: AdminHttpClient;
  onToast: (toast: ToastMessage) => void;
  selectedTenantId?: string;
}) {
  const { t } = useTranslation();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [tab, setTab] = useState<TabId>('settings');
  const [admins, setAdmins] = useState<PartnerAdminView[]>([]);
  const [tenantAssignment, setTenantAssignment] = useState<TenantAssignment | undefined>();
  const [modelKeys, setModelKeys] = useState<string[]>([]);
  const [customModels, setCustomModels] = useState<PartnerCustomModelSummary[]>([]);
  const [connectorKeys, setConnectorKeys] = useState<string[]>([]);
  const [partnerTenantCounts, setPartnerTenantCounts] = useState<Record<string, number>>({});
  const { busy, run } = useAsyncAction(onToast, t);

  // create-partner form
  const [name, setName] = useState('');
  const [cap, setCap] = useState('');
  // edit-cap field for the selected partner — kept separate from the create
  // form so selecting a partner shows ITS cap and "Save cap" can't overwrite
  // with leftover create-form input.
  const [editCap, setEditCap] = useState('');

  // grant-admin form
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminPassword, setAdminPassword] = useState('');

  const selected = partners.find((p) => p.id === selectedId);
  const selectedTenantCount = selected ? partnerTenantCounts[selected.id] ?? 0 : 0;

  // Switching partners returns to the first tab. Leaving, say, "Model
  // deployment" open across a partner change shows the new partner's data
  // under the tab the operator opened for the previous one.
  useEffect(() => {
    setTab('settings');
  }, [selectedId]);
  const modelScopeOptions = useMemo(() => {
    const options = new Map<string, { key: string; label: string }>();
    for (const modelId of tenantAssignment?.assignableModelIds ?? tenantAssignment?.allowedModelIds ?? []) {
      const key = partnerScopeKeyFromCatalogId(modelId, selectedTenantId);
      if (!options.has(key)) {
        options.set(key, {
          key,
          label: modelId
        });
      }
    }
    for (const model of customModels) {
      const key = `custom:${model.modelKey}`;
      options.set(key, { key, label: model.displayName });
    }
    return [...options.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [tenantAssignment?.allowedModelIds, tenantAssignment?.assignableModelIds, selectedTenantId, customModels]);
  const modelScopeOptionKeys = useMemo(() => new Set(modelScopeOptions.map((option) => option.key)), [modelScopeOptions]);
  const selectedModelKeys = useMemo(
    () => modelKeys.filter((key) => modelScopeOptionKeys.has(key)),
    [modelKeys, modelScopeOptionKeys]
  );

  const fail = useCallback(
    (error: unknown) => onToast({ tone: 'error', message: describeError(error, t) }),
    [onToast, t]
  );

  const loadPartners = useCallback(async () => {
    try {
      setPartners(await http.listPartners());
    } catch (error) {
      fail(error);
      return;
    }
    // Tenant counts only gate the delete button (the server re-enforces
    // partner_still_owns_tenants), so a tenants-call failure must not blank
    // the partner list — degrade to no counts instead.
    try {
      const tenantList = await http.getTenants();
      const counts: Record<string, number> = {};
      for (const tenant of tenantList) {
        if (tenant.partnerId) counts[tenant.partnerId] = (counts[tenant.partnerId] ?? 0) + 1;
      }
      setPartnerTenantCounts(counts);
    } catch {
      setPartnerTenantCounts({});
    }
  }, [http, fail]);

  const loadTenantAssignment = useCallback(async () => {
    if (!selectedTenantId) {
      setTenantAssignment(undefined);
      return;
    }
    try {
      setTenantAssignment(await http.getTenantAssignment(selectedTenantId));
    } catch (error) {
      fail(error);
    }
  }, [http, selectedTenantId, fail]);

  const loadDetail = useCallback(
    async (partnerId: string) => {
      try {
        const [adminList, keys, models, connectors] = await Promise.all([
          http.listPartnerAdmins(partnerId),
          http.getPartnerModelScope(partnerId),
          http.listPartnerCustomModels(partnerId),
          http.getPartnerMcpConnectorScope(partnerId)
        ]);
        setAdmins(adminList);
        setModelKeys(keys);
        setCustomModels(models);
        setConnectorKeys(connectors);
      } catch (error) {
        fail(error);
      }
    },
    [http, fail]
  );

  useEffect(() => {
    void loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    void loadTenantAssignment();
  }, [loadTenantAssignment]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  // Sync the edit-cap field to the selected partner's current cap (re-syncs
  // after a save reloads the list). Unlimited → empty input.
  useEffect(() => {
    const partner = partners.find((p) => p.id === selectedId);
    setEditCap(partner?.tenantCap === undefined ? '' : String(partner.tenantCap));
  }, [selectedId, partners]);

  function createPartner(event: FormEvent) {
    event.preventDefault();
    if (!isValidTenantName(name.trim())) {
      onToast({ tone: 'error', message: t('platform.tenantNameRule') });
      return;
    }
    const parsed = parseCap(cap, undefined);
    if (!parsed.ok) {
      onToast({ tone: 'error', message: t('partners.capInvalid') });
      return;
    }
    void run(async () => {
      const created = await http.createPartner({ name: name.trim(), tenantCap: parsed.value ?? undefined });
      setName('');
      setCap('');
      await loadPartners();
      setSelectedId(created.id);
    }, { successKey: 'partners.created' });
  }

  function saveCap(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    const parsed = parseCap(editCap, null);
    if (!parsed.ok) {
      onToast({ tone: 'error', message: t('partners.capInvalid') });
      return;
    }
    void run(async () => {
      await http.updatePartner(selected.id, { tenantCap: parsed.value ?? null });
      await loadPartners();
    }, { successKey: 'partners.capSaved' });
  }

  function setStatus(status: 'active' | 'suspended') {
    if (!selected) return;
    void run(async () => {
      await http.updatePartner(selected.id, { status });
      await loadPartners();
    }, { successKey: 'partners.statusSaved' });
  }

  function deletePartner() {
    if (!selected) return;
    const confirmName = window.prompt(t('partners.confirmDelete', { name: selected.name })) ?? '';
    if (!confirmName) return;
    void run(async () => {
      await http.deletePartner(selected.id, confirmName);
      setSelectedId(undefined);
      setAdmins([]);
      setModelKeys([]);
      setCustomModels([]);
      setConnectorKeys([]);
      await loadPartners();
    }, { successKey: 'partners.deleted' });
  }

  function toggleCustomModels() {
    if (!selected) return;
    void run(async () => {
      await http.updatePartner(selected.id, { allowCustomModels: !selected.allowCustomModels });
      await loadPartners();
    }, { successKey: 'partners.customModelsSaved' });
  }

  function grantAdmin(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    void run(async () => {
      await http.grantPartnerAdmin(selected.id, {
        email: adminEmail.trim(),
        name: adminName.trim(),
        password: adminPassword
      });
      setAdminEmail('');
      setAdminName('');
      setAdminPassword('');
      await loadDetail(selected.id);
    }, { successKey: 'partners.adminGranted' });
  }

  function revokeAdmin(userId: string) {
    if (!selected) return;
    void run(async () => {
      await http.revokePartnerAdmin(selected.id, userId);
      await loadDetail(selected.id);
    }, { successKey: 'partners.adminRevoked' });
  }


  function saveModelScope(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    void run(async () => {
      const saved = await http.setPartnerModelScope(selected.id, selectedModelKeys);
      setModelKeys(saved.filter((key) => modelScopeOptionKeys.has(key)));
    }, { successKey: 'partners.modelScopeSaved' });
  }

  function saveConnectorScope(event: FormEvent) {
    event.preventDefault();
    if (!selected) return;
    void run(async () => {
      const saved = await http.setPartnerMcpConnectorScope(selected.id, connectorKeys);
      setConnectorKeys(saved);
    }, { successKey: 'partners.connectorScopeSaved' });
  }

  return (
    <section className="content-stack">
      <section className="panel">
        <h1>{t('partners.platformManagementTitle')}</h1>
      </section>
      <PartnerCreateForm
        name={name}
        cap={cap}
        busy={busy}
        onNameChange={setName}
        onCapChange={setCap}
        onSubmit={createPartner}
      />

      <PartnersTable partners={partners} busy={busy} onSelect={setSelectedId} />

      {selected && (
        <div className="content-stack">
          {/* Tabs stay mounted (hidden, not unmounted) so an edited cap survives
              a look at another tab. The
              wrappers carry no layout class on purpose: an author `display`
              rule beats the UA `[hidden]` rule, so a classed wrapper would
              stay visible (see .tab-strip[hidden] in tables.css). */}
          <TabStrip
            tabs={TABS.map((item) => ({ id: item.id, label: t(item.label) }))}
            value={tab}
            onChange={setTab}
            ariaLabel={selected.name}
          />
          <div hidden={tab !== 'settings'}>
            <PartnerSettingsPanel
              selected={selected}
              selectedTenantCount={selectedTenantCount}
              editCap={editCap}
              busy={busy}
              onEditCapChange={setEditCap}
              onSaveCap={saveCap}
              onToggleStatus={() => setStatus(selected.status === 'active' ? 'suspended' : 'active')}
              onToggleCustomModels={toggleCustomModels}
              onDelete={deletePartner}
            />
          </div>

          <div hidden={tab !== 'admins'}>
            <PartnerAdminsTab
              admins={admins}
              adminEmail={adminEmail}
              adminName={adminName}
              adminPassword={adminPassword}
              busy={busy}
              onAdminEmailChange={setAdminEmail}
              onAdminNameChange={setAdminName}
              onAdminPasswordChange={setAdminPassword}
              onGrantAdmin={grantAdmin}
              onRevokeAdmin={revokeAdmin}
            />
          </div>

          <div hidden={tab !== 'modelScope'}>
            <PartnerModelScopeTab
              modelScopeOptions={modelScopeOptions}
              modelScopeOptionKeys={modelScopeOptionKeys}
              selectedModelKeys={selectedModelKeys}
              busy={busy}
              setModelKeys={setModelKeys}
              onSave={saveModelScope}
            />
          </div>

          <div hidden={tab !== 'connectorScope'}>
            <PartnerConnectorTab connectorKeys={connectorKeys} busy={busy} setConnectorKeys={setConnectorKeys} onSave={saveConnectorScope} />
          </div>
        </div>
      )}
    </section>
  );
}
